import {
  BarChart3,
  ClipboardList,
  Target,
  BookOpen,
  TrendingUp,
  Clock,
} from "lucide-react";

export const PRODUCT_STATS = {
  sessionsScoredPct: 100,
};

export const PLATFORM_TOTALS = {
  activeQuestions: 20000,
  fullMocks: 25,
};

export const FEATURES = [
  {
    icon: BarChart3,
    title: "Percentile Analytics",
    desc: "See exactly where you rank. Track score trajectory, identify weak spots, and measure improvement over time — with real data, not guesswork.",
    tag: "CORE FEATURE",
  },
  {
    icon: ClipboardList,
    title: "Full & Mini Mock Exams",
    desc: "25 realistic full-length UCAT mocks, plus section practice. Timed to UCAT standards with scaled scoring and bell curves.",
    tag: "EXAM-ACCURATE",
  },
  {
    icon: Target,
    title: "Exam-Accurate Interface",
    desc: "Built to mirror the official UCAT client. Same layout, timer, calculator, and navigation. No exam day surprises.",
    tag: "SAME AS REAL EXAM",
  },
  {
    icon: BookOpen,
    title: "Public Study Guides",
    desc: "Clear UCAT section guides, strategy notes, and worked advice you can access before you even create an account.",
    tag: "FREE RESOURCES",
  },
  {
    icon: TrendingUp,
    title: "Personalised Insights",
    desc: "Your performance across 20,000 active UCAT questions drives your study plan. Know exactly what to practise, when to review, and where your time is best spent.",
    tag: "DATA-DRIVEN",
  },
  {
    icon: Clock,
    title: "Time Performance Analysis",
    desc: "Per-question and per-section timing breakdowns. Understand your pace, spot time sinks, and optimise under exam conditions.",
    tag: "DETAILED",
  },
];

export const UCAT_SECTIONS = [
  {
    code: "VR",
    name: "Verbal Reasoning",
    time: "22 min",
    questions: 44,
    color: "bg-blue-500",
    desc: "True/False/Can't Tell & free text answers.",
    path: "/ucat/verbal-reasoning",
  },
  {
    code: "DM",
    name: "Decision Making",
    time: "37 min",
    questions: 35,
    color: "bg-emerald-500",
    desc: "Logic, syllogisms, Venn diagrams.",
    path: "/ucat/decision-making",
  },
  {
    code: "QR",
    name: "Quantitative Reasoning",
    time: "26 min",
    questions: 36,
    color: "bg-violet-500",
    desc: "Data interpretation under time pressure.",
    path: "/ucat/quantitative-reasoning",
  },
  {
    code: "SJT",
    name: "Situational Judgement",
    time: "26 min",
    questions: 69,
    color: "bg-orange-500",
    desc: "Professional scenarios & ranking tasks.",
    path: "/ucat/situational-judgement",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "The analytics showed me I was spending too long on VR passages. Once I adjusted, my score jumped 340 points in 6 weeks. No other platform gave me that level of insight.",
    author: "Sarah M.",
    uni: "King's College London",
    before: "2240",
    after: "2580",
    avatar: "SM",
  },
  {
    quote:
      "The percentile tracking was eye-opening. I could see exactly where I ranked in QR after every session. Went from 560 to 830 by focusing on what the data told me.",
    author: "James L.",
    uni: "University of Edinburgh",
    before: "2290",
    after: "2610",
    avatar: "JL",
  },
  {
    quote:
      "I switched from another platform after two months. The interface here is identical to the real exam, and the performance data is on another level. Felt completely ready on test day.",
    author: "Priya K.",
    uni: "Manchester Medical School",
    before: "2160",
    after: "2500",
    avatar: "PK",
  },
  {
    quote:
      "The section-specific mini mocks with time analysis helped me target DM specifically. Six weeks later, it went from my worst section to my best. The data doesn't lie.",
    author: "Tom R.",
    uni: "University of Bristol",
    before: "2210",
    after: "2500",
    avatar: "TR",
  },
];

export const FAQS = [
  {
    q: "How accurate is the exam interface?",
    a: "Our exam interface is built to exactly replicate the official Pearson VUE UCAT client — same layout, timer behaviour, calculator, and navigation shortcuts. Students consistently report feeling completely prepared on exam day.",
  },
  {
    q: "Will my score actually improve?",
    a: "Consistent, targeted practice is what moves UCAT scores. Med900 tracks your percentile, time per question, and weak categories after every session so you know exactly where to focus next. We stand behind the platform — if your score doesn't improve, we'll extend your access at no cost.",
  },
  {
    q: "What makes Med900 different from other platforms?",
    a: "Med900 was built from the ground up with analytics at its core. Alongside 20,000 active UCAT questions and 25 full mocks, we give you percentile tracking, time analysis per question, score trajectory charts, bell curve distributions, and section-level insights. Every session you take feeds into a personalised performance profile that tells you exactly where to focus.",
  },
  {
    q: "When should I start practising?",
    a: "Most students see the best results starting 8–12 weeks before their exam. Our analytics will build a personalised study plan based on your initial performance, so you focus on what matters most.",
  },
  {
    q: "Is the content up to date?",
    a: "All content is updated for the current UCAT ANZ format (four subtests, cognitive total out of 2700). We continuously review and add new questions so practice stays aligned with the live exam.",
  },
];

export function getCtaHighlights(platformStats) {
  const questionCount = platformStats?.practice_questions ?? PLATFORM_TOTALS.activeQuestions;
  const fullMocks = platformStats?.full_mocks ?? PLATFORM_TOTALS.fullMocks;

  return [
    `${questionCount.toLocaleString()} active questions`,
    `${fullMocks} full mocks`,
    "Time analysis",
    "Percentile tracking",
  ];
}
